import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { Conversations, Users, type Conversation, type ConversationMember, type User } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useChatStore } from '@/stores/chat';
import UserAvatar from './UserAvatar';

interface Props {
  conversation: Conversation;
  members: ConversationMember[];
  onClose: () => void;
}

export default function GroupSettingsModal({ conversation, members, onClose }: Props) {
  const { user: me } = useAuth();
  const setMembers = useChatStore((s) => s.setMembers);
  const upsertMember = useChatStore((s) => s.upsertMember);
  const removeMemberFromStore = useChatStore((s) => s.removeMember);
  const upsertConversation = useChatStore((s) => s.upsertConversation);

  const [name, setName] = useState(conversation.name ?? '');
  const [imageUrl, setImageUrl] = useState(conversation.imageUrl ?? '');
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<User[]>([]);
  const [savingName, setSavingName] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [adding, setAdding] = useState<string | null>(null);

  const myRole = useMemo(() => members.find((m) => m.userId === me?.id)?.role ?? 'member', [members, me]);
  const canEdit = myRole === 'owner' || myRole === 'manager';
  const canManageRoles = myRole === 'owner';

  useEffect(() => {
    if (!search.trim()) {
      setResults([]);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(() => {
      Users.search(search.trim())
        .then((r) => !cancelled && setResults(r.users ?? []))
        .catch(() => !cancelled && setResults([]));
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [search]);

  async function saveName() {
    if (!canEdit) return;
    setSavingName(true);
    setErr(null);
    try {
      const { conversation: updated } = await Conversations.update(conversation.id, {
        name: name.trim() || undefined,
        imageUrl: imageUrl.trim() || undefined,
      });
      upsertConversation({ ...conversation, ...updated });
    } catch (e) {
      const m = e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : 'Failed';
      setErr(m);
    } finally {
      setSavingName(false);
    }
  }

  async function addMember(u: User) {
    setAdding(u.id);
    setErr(null);
    try {
      await Conversations.addMember(conversation.id, u.id);
      const member: ConversationMember = { userId: u.id, role: 'member', user: u };
      upsertMember(conversation.id, member);
      setSearch('');
      setResults([]);
    } catch (e) {
      const m = e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : 'Failed';
      setErr(m);
    } finally {
      setAdding(null);
    }
  }

  async function removeMember(userId: string) {
    setErr(null);
    try {
      await Conversations.removeMember(conversation.id, userId);
      removeMemberFromStore(conversation.id, userId);
    } catch (e) {
      const m = e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : 'Failed';
      setErr(m);
    }
  }

  async function setRole(userId: string, role: 'manager' | 'member') {
    setErr(null);
    try {
      await Conversations.updateMemberRole(conversation.id, userId, role);
      const cur = useChatStore.getState().membersByConversation.get(conversation.id) ?? [];
      setMembers(
        conversation.id,
        cur.map((m) => (m.userId === userId ? { ...m, role } : m)),
      );
    } catch (e) {
      const m = e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : 'Failed';
      setErr(m);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Group settings"
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="card flex max-h-[90vh] w-full max-w-md flex-col">
        <div className="flex items-center justify-between border-b border-ink-100 px-4 py-3 dark:border-ink-700">
          <h2 className="text-lg font-semibold">Group settings</h2>
          <button type="button" aria-label="Close" className="btn-ghost px-2" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="nice-scroll flex-1 space-y-4 overflow-y-auto p-4">
          <fieldset disabled={!canEdit} className={clsx(!canEdit && 'opacity-60')}>
            <legend className="mb-1 text-sm font-medium">Name & image</legend>
            <input
              className="input mb-2"
              value={name}
              maxLength={64}
              onChange={(e) => setName(e.target.value)}
              placeholder="Group name"
            />
            <input
              className="input"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="Image URL (optional)"
            />
            <div className="mt-2 flex justify-end">
              <button type="button" className="btn-primary" onClick={saveName} disabled={!canEdit || savingName}>
                {savingName ? 'Saving…' : 'Save'}
              </button>
            </div>
          </fieldset>

          <div>
            <h3 className="mb-1 text-sm font-medium">Members ({members.length})</h3>
            <ul className="divide-y divide-ink-100 dark:divide-ink-700">
              {members.map((m) => (
                <li key={m.userId} className="flex items-center gap-2 py-2">
                  <UserAvatar name={m.user?.displayName || m.user?.username} src={m.user?.avatarUrl} size={32} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{m.user?.displayName || m.user?.username || m.userId}</div>
                    <div className="truncate text-xs text-ink-500 dark:text-ink-300">{m.role}</div>
                  </div>
                  {canManageRoles && m.userId !== me?.id && m.role !== 'owner' && (
                    <button
                      type="button"
                      className="btn-ghost px-2 text-xs"
                      onClick={() => setRole(m.userId, m.role === 'manager' ? 'member' : 'manager')}
                    >
                      {m.role === 'manager' ? 'Demote' : 'Promote'}
                    </button>
                  )}
                  {(canEdit || me?.id === m.userId) && m.userId !== me?.id && (
                    <button
                      type="button"
                      className="btn-ghost px-2 text-xs text-red-600 dark:text-red-400"
                      onClick={() => removeMember(m.userId)}
                    >
                      Remove
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="mb-1 text-sm font-medium">Add member</h3>
            <input
              type="search"
              className="input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by username…"
            />
            {search.trim() && (
              <ul className="nice-scroll mt-1 max-h-48 overflow-y-auto rounded-lg border border-ink-100 dark:border-ink-700">
                {results
                  .filter((u) => !members.some((m) => m.userId === u.id))
                  .map((u) => (
                    <li key={u.id}>
                      <button
                        type="button"
                        disabled={adding === u.id}
                        onClick={() => addMember(u)}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-ink-100 dark:hover:bg-ink-700"
                      >
                        <UserAvatar name={u.displayName || u.username} src={u.avatarUrl} size={28} />
                        <span className="flex-1 truncate">{u.displayName || u.username}</span>
                        <span className="text-xs text-brand-600 dark:text-brand-300">{adding === u.id ? 'Adding…' : 'Add'}</span>
                      </button>
                    </li>
                  ))}
              </ul>
            )}
          </div>

          {err && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/60 dark:text-red-300">{err}</div>}
        </div>
      </div>
    </div>
  );
}
